// Real HTTP adapter for the R-value (위험지수) threshold card. PRD 09 §3.4/§4.4.
// Maps the server error envelope { error: { code, message, fields } } to RiskRangeApiError.
//
// The endpoint that shipped is /admin/biometric-risk-bands, and it speaks in BANDS, not in the
// card's shape: both GET and PUT reply with a LIST — [{ band: 'LOW'|'MID'|'HIGH', minValue, maxValue }]
// in seed order — while PUT takes the card's own { low, mid, high } object as its body. So the reply
// is translated back into the card's shape here, at the wire boundary, and the rest of the feature
// keeps working in { low, mid, high } (SHCT-681).
//   GET /admin/biometric-risk-bands  → { data: [ {band, minValue, maxValue} × 3 ] }
//   PUT /admin/biometric-risk-bands  body { low, mid, high } → { data: [ … × 3 ] }
// The whole object is saved in one PUT (single card). Activated by VITE_USE_MOCK_API=false;
// see risk-range.adapter.ts.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  RiskRangeApiError,
  type RiskRange,
  type RiskRangeApi,
  type RiskRangeApiErrorCode,
  type RiskInterval,
} from '../types/risk-range.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
  };
}

function isInterval(v: unknown): v is RiskInterval {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as RiskInterval).min === 'number' &&
    typeof (v as RiskInterval).max === 'number'
  );
}

// A malformed body (wrong envelope, SPA fallback HTML, partial object) would make the form
// seed with `undefined` and crash — surface a clean load error instead.
function isRiskRange(v: unknown): v is RiskRange {
  return (
    typeof v === 'object' &&
    v !== null &&
    isInterval((v as RiskRange).low) &&
    isInterval((v as RiskRange).mid) &&
    isInterval((v as RiskRange).high)
  );
}

/** One band row as the server sends it. `band` is the English code; the card owns the Korean label. */
interface RiskBandWire {
  band: string;
  minValue: number;
  maxValue: number;
}

const BAND_TO_INTERVAL: Readonly<Record<string, keyof RiskRange>> = {
  LOW: 'low',
  MID: 'mid',
  HIGH: 'high',
};

/**
 * The three band rows, folded back into the card's { low, mid, high } shape.
 *
 * A missing band is not filled in with a default: the card would then show a boundary the server never
 * stored, and the operator would save it back believing they had confirmed it. An incomplete list is a
 * load error instead.
 */
function toRiskRange(rows: unknown): RiskRange | null {
  if (!Array.isArray(rows)) return null;
  const out: Partial<RiskRange> = {};
  for (const row of rows as RiskBandWire[]) {
    const key = row && typeof row.band === 'string' ? BAND_TO_INTERVAL[row.band] : undefined;
    if (!key) continue;
    if (typeof row.minValue !== 'number' || typeof row.maxValue !== 'number') continue;
    out[key] = { min: row.minValue, max: row.maxValue };
  }
  return isRiskRange(out) ? out : null;
}

function mapError(e: unknown): never {
  // A typed error thrown by a shape guard above must pass through untouched. It carries no axios
  // `response`, so re-mapping it would drop its message and re-label it NETWORK/UNKNOWN.
  if (e instanceof RiskRangeApiError) throw e;
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  // No `response` means the request never got an HTTP reply — offline, DNS, CORS, or timeout.
  // Error Standard §3 / CLAUDE.md rule 3: a distinct NETWORK code, not a generic unknown error.
  if (!candidate.response) {
    throw new RiskRangeApiError('NETWORK', '네트워크 연결을 확인해주세요.');
  }
  const payload = candidate.response.data?.error;
  throw new RiskRangeApiError(
    (payload?.code as RiskRangeApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    payload?.fields,
  );
}

export const riskRangeHttpApi: RiskRangeApi = {
  async getRange(): Promise<RiskRange> {
    try {
      const res = await httpClient.get<ApiEnvelope<unknown>>(endpoints.riskBands);
      const range = toRiskRange(res.data?.data);
      if (!range) {
        throw new RiskRangeApiError(
          'UNKNOWN',
          '데이터기준값 범위를 불러올 수 없습니다.',
        );
      }
      return range;
    } catch (e) {
      mapError(e);
    }
  },

  async updateRange(range: RiskRange): Promise<RiskRange> {
    try {
      // The body is the card's own shape (the server's request record mirrors it field for field);
      // only the reply comes back as bands.
      const res = await httpClient.put<ApiEnvelope<unknown>>(
        endpoints.riskBands,
        range,
      );
      const saved = toRiskRange(res.data?.data);
      if (!saved) {
        throw new RiskRangeApiError(
          'UNKNOWN',
          '데이터기준값 범위를 저장했으나 응답을 읽을 수 없습니다.',
        );
      }
      return saved;
    } catch (e) {
      mapError(e);
    }
  },
};
